// src/features/changelog/components/ReleaseCard.jsx
import React, { useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CHANGE_TYPES } from '../utils/changelog';
import ReleaseChangeList from './ReleaseChangeList';

// Space kept above a release when scrolling to it, so the fixed app bar
// doesn't cover its header
const SCROLL_OFFSET = 80;

export default function ReleaseCard({ release, isLatest, expanded, onChange }) {
  const { t } = useTranslation(['common']);
  const rootRef = useRef(null);
  const openedByUser = useRef(false);

  const handleChange = (event, isExpanded) => {
    openedByUser.current = isExpanded;
    onChange(event, isExpanded);
  };

  // Opening a release collapses the one that was open. If that one was above,
  // this release slides up and its header can end up off screen, so bring it
  // back once both transitions are done. Not on the initial auto-expand.
  const handleEntered = () => {
    if (!openedByUser.current) return;
    openedByUser.current = false;
    if (rootRef.current?.getBoundingClientRect().top < SCROLL_OFFSET) {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const countsLabel = CHANGE_TYPES
    .filter((type) => release.counts[type] > 0)
    .map((type) => t(`common:changelog.counts.${type}`, { count: release.counts[type] }))
    .join(' · ');

  return (
    <Accordion
      ref={rootRef}
      expanded={expanded}
      onChange={handleChange}
      slotProps={{ transition: { unmountOnExit: true, onEntered: handleEntered } }}
      sx={{
        scrollMarginTop: SCROLL_OFFSET,
        '&:before': { display: 'none' },
        borderLeft: '3px solid',
        borderLeftColor: isLatest ? 'success.main' : 'transparent',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Chip
              label={`v${release.version}`}
              color="primary"
              size="small"
              sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
            />
            {isLatest && (
              <Chip
                label={t('common:changelog.latest')}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            <Typography variant="h6" component="span" sx={{ ml: 0.5 }}>
              {release.title}
            </Typography>
          </Box>

          {countsLabel && (
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {countsLabel}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
            {release.summary}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <ReleaseChangeList groups={release.groups} />
      </AccordionDetails>
    </Accordion>
  );
}
